import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { AlertTriangle } from "lucide-react";

const ConfirmContext = createContext(null);

/**
 * Global in-app confirmation dialog.
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: "Delete?", message: "…", confirmText: "Delete", danger: true });
 *   if (!ok) return;
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, message, confirmText, cancelText, danger }
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts.title || "Are you sure?",
        message: opts.message || "",
        confirmText: opts.confirmText || "Confirm",
        cancelText: opts.cancelText || "Cancel",
        danger: !!opts.danger,
      });
    });
  }, []);

  const settle = (ok) => {
    setState(null);
    if (resolverRef.current) resolverRef.current(ok);
    resolverRef.current = null;
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!state} onOpenChange={(v) => { if (!v) settle(false); }}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <div className="flex items-start gap-3">
              {state?.danger && (
                <div className="w-10 h-10 rounded-2xl bg-[#FEE2E2] flex items-center justify-center text-[#9F1239] flex-shrink-0">
                  <AlertTriangle size={20} />
                </div>
              )}
              <div className="min-w-0">
                <DialogTitle className="font-display text-2xl text-[#2C2A29]">{state?.title}</DialogTitle>
                {state?.message && (
                  <DialogDescription className="text-[#7A7571] text-sm mt-2 leading-relaxed">
                    {state.message}
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              type="button"
              onClick={() => settle(false)}
              data-testid="confirm-cancel"
              className="btn-secondary h-12"
            >
              {state?.cancelText}
            </button>
            <button
              type="button"
              onClick={() => settle(true)}
              data-testid="confirm-ok"
              className={`h-12 ${state?.danger ? "btn-danger" : "btn-primary"} inline-flex items-center justify-center`}
            >
              {state?.confirmText}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
